let audioContext: AudioContext | null = null;
let pendingGestureUnlock: (() => void) | null = null;

// Module-scoped (not per-hook-instance): more than one
// useNotificationSubscription can be mounted at once (e.g. the persistent
// NotificationCenter plus the /notifications page), and both would
// otherwise treat the same notification id as new and each play the chime.
const recentlyPlayedIds: string[] = [];
const MAX_TRACKED_IDS = 50;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined" || typeof window.AudioContext === "undefined") {
    return null;
  }
  audioContext ??= new AudioContext();
  return audioContext;
}

/**
 * Resumes (or lazily creates) the shared AudioContext. Browsers only allow
 * this from within a real user gesture, so call this from the click handler
 * that turns "Sound notifications" on — a notification arriving later isn't
 * a gesture and can't unlock a suspended context on its own.
 */
export function unlockNotificationSoundContext() {
  const ctx = getAudioContext();
  if (ctx?.state === "suspended") {
    void ctx.resume();
  }
}

/**
 * Arms one genuine user gesture to unlock the shared AudioContext when sound
 * was already enabled before this page loaded. Realtime callbacks cannot
 * satisfy browser gesture requirements, so sound must not wait for one to
 * arrive before attempting its first resume.
 */
export function armNotificationSoundUnlock() {
  if (typeof window === "undefined" || pendingGestureUnlock) return;

  const unlockFromGesture = () => {
    pendingGestureUnlock?.();
    unlockNotificationSoundContext();
  };

  pendingGestureUnlock = () => {
    window.removeEventListener("click", unlockFromGesture, true);
    window.removeEventListener("keydown", unlockFromGesture, true);
    pendingGestureUnlock = null;
  };

  window.addEventListener("click", unlockFromGesture, true);
  window.addEventListener("keydown", unlockFromGesture, true);
}

/**
 * Plays a short two-tone chime for the "sound notifications" preference,
 * deduped per notification id so the same event doesn't double-play across
 * concurrently mounted subscriptions.
 */
export function playNotificationSound(id?: string) {
  if (id) {
    if (recentlyPlayedIds.includes(id)) return;
    recentlyPlayedIds.push(id);
    if (recentlyPlayedIds.length > MAX_TRACKED_IDS) {
      recentlyPlayedIds.shift();
    }
  }

  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    if (ctx.state === "suspended") {
      void ctx.resume();
    }

    const now = ctx.currentTime;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.frequency.setValueAtTime(1108, now + 0.09);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);

    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.2);
  } catch (error) {
    console.error("Failed to play notification sound:", error);
  }
}
